import { X } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { EntityReferenceId, ShoppingList } from "../../../app/types.js";
import { api, postJson } from "../../../lib/api.js";
import { useHousehold } from "../../context.js";
import { ListSheet, type SheetListItem } from "./ListSheet.js";

// Global shopping-list dialog: any feature can open a list (by id or name) in
// a spreadsheet editor without leaving its own page. The ListSheet manages its
// own optimistic, offline-aware sync, so the dialog only supplies the list.

export function sortSheetItems<T extends SheetListItem>(items: T[]): T[] {
  return [...items].sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0) || String(left.id).localeCompare(String(right.id)));
}

type ListSheetDialogContextValue = {
  openList: (listId: EntityReferenceId) => void;
  openListByName: (name: string, options?: { createIfMissing?: boolean; projectId?: EntityReferenceId }) => Promise<void>;
  closeList: () => void;
};

const ListSheetDialogContext = createContext<ListSheetDialogContextValue | null>(null);

export function useListSheetDialog() {
  const context = useContext(ListSheetDialogContext);
  if (!context) throw new Error("useListSheetDialog must be used inside ListSheetDialogProvider");
  return context;
}

export function ListSheetDialogProvider({ children }: { children: ReactNode }) {
  const { loadShopping, setError, shoppingLists } = useHousehold();
  const { t } = useTranslation("common");
  // A new language means a new `t` (react-i18next re-mints it from getFixedT on
  // languageChanged), and this provider is mounted beside the tab shell, so its
  // effect is live from every page. The on-demand fetch below wants nothing to
  // do with that, and reads its fallback sentence through the ref.
  const tRef = useRef(t);
  tRef.current = t;
  const [openListId, setOpenListId] = useState<EntityReferenceId | null>(null);
  const [loading, setLoading] = useState(false);

  const list = openListId ? shoppingLists.find((candidate) => candidate.id === openListId) ?? null : null;

  const openList = useCallback((listId: EntityReferenceId) => {
    setOpenListId(listId);
  }, []);

  const closeList = useCallback(() => {
    setOpenListId(null);
  }, []);

  const openListByName = useCallback(async (name: string, options: { createIfMissing?: boolean; projectId?: EntityReferenceId } = {}) => {
    setLoading(true);
    try {
      const lists = await api<ShoppingList[]>("/api/v1/shopping-lists");
      const normalizedName = name.trim().toLowerCase();
      let target = lists.find((candidate) => (
        candidate.name.trim().toLowerCase() === normalizedName
        && (options.projectId ? candidate.projectId === options.projectId : true)
      )) ?? null;
      if (!target && options.createIfMissing) {
        target = options.projectId
          ? await postJson<ShoppingList>(`/api/v1/projects/${options.projectId}/shopping-lists`, { name })
          : await postJson<ShoppingList>("/api/v1/shopping-lists", { name });
      }
      if (!target) return;
      await loadShopping();
      setOpenListId(target.id);
    } catch (error) {
      setError(error instanceof Error ? error.message : t("listDialog.errors.open"));
    } finally {
      setLoading(false);
    }
  }, [loadShopping, setError, t]);

  // The dialog can open from pages that never load shopping data; fetch it on
  // demand so the list contents are available.
  //
  // `t` was a dependency, which made a language change re-enter this. The guard
  // usually absorbs that — once loadShopping has resolved the list is in
  // shoppingLists — but it does not on the path where the list never arrives (a
  // failed load, a deleted list): there the guard stays open, so every language
  // change re-fired a household-wide shopping fetch and republished the same
  // error over the shell.
  useEffect(() => {
    if (!openListId || shoppingLists.some((candidate) => candidate.id === openListId)) return;
    setLoading(true);
    loadShopping()
      .catch((error) => setError(error instanceof Error ? error.message : tRef.current("listDialog.errors.load")))
      .finally(() => setLoading(false));
  }, [loadShopping, openListId, setError, shoppingLists]);

  useEffect(() => {
    if (!openListId) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenListId(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [openListId]);

  const value = useMemo(() => ({ closeList, openList, openListByName }), [closeList, openList, openListByName]);
  const sortedItems = list ? sortSheetItems(list.items as SheetListItem[]) : [];

  return (
    <ListSheetDialogContext.Provider value={value}>
      {children}
      {openListId ? (
        <div className="modal-backdrop list-sheet-dialog-backdrop" role="presentation" onClick={closeList}>
          <div
            className="modal list-sheet-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={list?.name ?? t("listDialog.title")}
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span className="eyebrow">{t("listDialog.eyebrow")}</span>
                <h3>{list?.name ?? (loading ? t("listDialog.loading") : t("listDialog.title"))}</h3>
              </div>
              <button type="button" className="icon-button" aria-label={t("listDialog.close")} title={t("actions.close")} onClick={closeList}>
                <X size={16} />
              </button>
            </header>
            {list ? (
              <ListSheet
                items={sortedItems}
                listId={openListId}
                storageKey="dialog"
              />
            ) : (
              <p className="inline-note">{loading ? t("listDialog.loadingEllipsis") : t("listDialog.unavailable")}</p>
            )}
          </div>
        </div>
      ) : null}
    </ListSheetDialogContext.Provider>
  );
}
